import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, MessageSquare, Send, CheckCircle } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useIntersectionObserver } from "@/hooks/use-intersection-observer";
import { supabase } from "@/integrations/supabase/client";

const contactSchema = z.object({
  name: z.string().trim().min(2, "Name must be at least 2 characters").max(100, "Name must be less than 100 characters").regex(/^[a-zA-Z\s'-]+$/, "Name contains invalid characters"),
  email: z.string().trim().email("Invalid email address").max(255, "Email too long"),
  intake: z.string().min(1, "Please select an intake month"),
  message: z.string().trim().min(10, "Message must be at least 10 characters").max(2000, "Message must be less than 2000 characters")
});

const Contact = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    intake: "",
    message: ""
  });

  const [headerRef, headerVisible] = useIntersectionObserver();
  const [formRef, formVisible] = useIntersectionObserver({ rootMargin: "50px" });
  const [infoRef, infoVisible] = useIntersectionObserver({ rootMargin: "50px" });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate input
      const validatedData = contactSchema.parse(formData);

      // Save to database
      const { error } = await supabase
        .from('contact_submissions')
        .insert({
          name: validatedData.name,
          email: validatedData.email,
          intake: validatedData.intake,
          message: validatedData.message,
        });

      if (error) throw error;

      // Send email notification (don't block on failure)
      supabase.functions.invoke('send-notification-email', {
        body: {
          type: 'contact',
          data: validatedData
        }
      }).catch(err => console.error('Email notification failed:', err));

      setSubmitted(true);
      setFormData({
        name: "",
        email: "",
        intake: "",
        message: ""
      });
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.errors[0].message,
          variant: "destructive"
        });
      } else {
        toast({
          title: "Submission Failed",
          description: "Please try again or contact us directly at +61 0403 601 970.",
          variant: "destructive"
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-card rounded-2xl p-8 md:p-12 shadow-card">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                Thank You for Submitting!
              </h2>
              <p className="text-muted-foreground mb-6">
                We'll get back to you shortly. For more information, you can contact us via WhatsApp.
              </p>
                <Button asChild size="lg" className="bg-[#25D366] hover:bg-[#20BA5A]">
                  <a 
                    href="https://wa.me/+61403601970" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2"
                  >
                  <MessageSquare className="w-5 h-5" />
                  Chat with Us on WhatsApp
                </a>
              </Button>
              <div className="mt-6">
                <Button 
                  variant="outline" 
                  onClick={() => setSubmitted(false)}
                >
                  Submit Another Inquiry
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 md:py-24 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <header 
            ref={headerRef}
            className={`text-center mb-8 md:mb-12 transition-all duration-700 ${
              headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3 md:mb-4">
              Ready to{" "}
              <span className="text-secondary">Anchor Down</span>{" "}
              in Australia?
            </h2>
            <p className="text-base md:text-xl text-muted-foreground">
              <span className="hidden md:inline">Limited slots available per intake - book now to secure your personalised settlement experience.</span>
              <span className="md:hidden">Limited slots - book now!</span>
            </p>
          </header>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8 mb-8 md:mb-12">
            <div 
              ref={formRef}
              className={`bg-card rounded-2xl p-6 md:p-8 shadow-card transition-all duration-700 ${
                formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6" aria-label="Contact form for student services">
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Full Name
                  </label>
                  <Input 
                    placeholder="Enter your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Email Address
                  </label>
                  <Input 
                    type="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Preferred Intake
                  </label>
                  <Select 
                    value={formData.intake}
                    onValueChange={(value) => setFormData({ ...formData, intake: value })}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select intake month" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Feb 2025">February 2025</SelectItem>
                      <SelectItem value="July 2025">July 2025</SelectItem>
                      <SelectItem value="Nov 2025">November 2025</SelectItem>
                      <SelectItem value="Feb 2026">February 2026</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Message
                  </label>
                  <Textarea 
                    placeholder="Tell us about your plans..."
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full" 
                  size="lg"
                  disabled={loading}
                >
                  <Send className="w-4 h-4 mr-2" />
                  {loading ? "Sending..." : "Book My Spot"}
                </Button>
              </form>
            </div>

            <div 
              ref={infoRef}
              className={`space-y-6 transition-all duration-700 ${
                infoVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: '100ms' }}
            >
              <div className="bg-card rounded-2xl p-8 shadow-card hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      WhatsApp Us
                    </h3>
                    <p className="text-muted-foreground mb-3">
                      Get instant responses to your queries
                    </p>
                    <Button variant="outline" asChild>
                      <a href="https://wa.me/+61403601970" target="_blank" rel="noopener noreferrer">
                        Chat Now
                      </a>
                    </Button>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-2xl p-8 shadow-card hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      Email Us
                    </h3>
                    <p className="text-muted-foreground mb-3">
                      Send us detailed queries anytime
                    </p>
                    <a 
                      href="mailto:support@aceanchorau.com"
                      className="text-primary hover:underline"
                    >
                      support@aceanchorau.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center text-sm text-muted-foreground">
            <p>
              <strong>Payment Terms:</strong> All prices include 10% GST. 
              A non-refundable booking amount is required upfront. 
              Remaining balance is payable in Australia on arrival by card, cash, or payment link.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
